import { useEffect, useState } from 'react'
import {
  Alert, Badge, Button, Card, Checkbox, Container, Divider, Group, Loader, Modal, MultiSelect, Stack, Switch, Table, Text, TextInput, Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconBell, IconBroadcast, IconInfoCircle, IconSearch, IconUserCheck } from '@tabler/icons-react'
import type { PersonalDigestReport, TgChatBindingDto, TgNotificationSettingsDto } from '@ledokol/contracts'
import { AppLayout } from '../features/projects/app-shell'
import { useAuthStore } from '../features/auth/store'
import { fetchUsers } from '../shared/api/users'
import { broadcastTgStatus, fetchTgNotificationGroups, sendPersonalDigest, updateTgNotificationSettings } from '../shared/api/tg-bindings'

const SKIP_REASON: Record<string, string> = {
  no_tg: 'нет привязки Telegram',
  not_started: 'не нажал /start у бота',
}

const DAY_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/

/**
 * /admin/tg-notifications (#5 · 2E) — анти-спам ТГ-бота. Per-группа: окно тишины
 * (не слать пингующие авто вне рабочего времени/выходных) + время дайджеста + вкл/выкл.
 * Плюс кнопка мгновенной рассылки статуса во все группы.
 *
 * Гейт — ПРАВО tg_notifications.manage (не роль): admin по дефолту, делегируется через
 * RBAC-панель. Отдельно от admin-only /admin/tg-bindings (там привязки чат→проект).
 */
export function AdminTgNotificationsPage() {
  const { user } = useAuthStore()
  const [groups, setGroups] = useState<TgChatBindingDto[]>([])
  const [loading, setLoading] = useState(true)
  const [broadcastOpen, setBroadcastOpen] = useState(false)
  const [broadcasting, setBroadcasting] = useState(false)
  const [query, setQuery] = useState('')

  async function reload() {
    setLoading(true)
    try {
      setGroups(await fetchTgNotificationGroups())
    } catch (e) {
      notifications.show({ color: 'red', title: 'Ошибка', message: String(e) })
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    if (user?.permissions.includes('tg_notifications.manage')) void reload()
  }, [user])

  if (!user) return null
  if (!user.permissions.includes('tg_notifications.manage')) {
    return (
      <AppLayout>
        <Container><Text c="red" mt="md">Доступ только для права tg_notifications.manage.</Text></Container>
      </AppLayout>
    )
  }

  const active = groups.filter((g) => g.is_active && g.project)
  const q = query.trim().toLowerCase()
  const filtered = q
    ? active.filter((g) =>
        [g.project?.name, g.chat_title, g.topic_name, g.tg_chat_id]
          .some((v) => v != null && String(v).toLowerCase().includes(q)),
      )
    : []

  async function doBroadcast() {
    setBroadcasting(true)
    try {
      const r = await broadcastTgStatus()
      notifications.show({ color: 'green', message: `Рассылка запущена — ${r.active_chats} групп(ы)` })
      setBroadcastOpen(false)
    } catch (e) {
      notifications.show({ color: 'red', title: 'Ошибка', message: String(e) })
    } finally {
      setBroadcasting(false)
    }
  }

  return (
    <AppLayout>
      <Container size="xl" py="md">
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <IconBell size={24} />
            <Title order={2}>Уведомления в Telegram</Title>
          </Group>
          <Button
            leftSection={<IconBroadcast size={16} />}
            variant="light"
            onClick={() => setBroadcastOpen(true)}
            disabled={active.length === 0}
          >
            Разослать статус сейчас
          </Button>
        </Group>

        <Alert icon={<IconInfoCircle size={16} />} color="blue" mb="md" variant="light">
          Окно тишины глушит автоматические сообщения бота в группу (утренний дайджест,
          теги «на проверке», статусы при переходах) вне рабочего времени и в выходные —
          они не теряются, а доезжают, когда окно откроется. Ответы на команды («@bot статус»,
          «готово») приходят всегда.
        </Alert>

        <PersonalDigestSection />
        <Divider my="lg" label="Окно тишины и дайджест по группам" labelPosition="center" />

        {loading ? (
          <Loader />
        ) : active.length === 0 ? (
          <Text c="dimmed">Нет активных привязанных групп. Привязки настраиваются в разделе «Чаты Telegram».</Text>
        ) : (
          <>
            <TextInput
              leftSection={<IconSearch size={16} />}
              placeholder="Найти группу или проект по названию"
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
              mb="md"
              maw={480}
            />
            {!q ? (
              <Text c="dimmed">
                Введите название группы или проекта, чтобы открыть его настройки. Всего активных групп: {active.length}.
              </Text>
            ) : filtered.length === 0 ? (
              <Text c="dimmed">Ничего не найдено по запросу «{query.trim()}».</Text>
            ) : (
              <Table striped withTableBorder verticalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Группа / проект</Table.Th>
                    <Table.Th>Окно тишины</Table.Th>
                    <Table.Th>Дайджест</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filtered.map((g) => <GroupRow key={g.id} group={g} onSaved={reload} />)}
                </Table.Tbody>
              </Table>
            )}
          </>
        )}
      </Container>

      <Modal opened={broadcastOpen} onClose={() => setBroadcastOpen(false)} title="Мгновенная рассылка">
        <Stack>
          <Text>
            Отправить сводку статуса во <b>все {active.length}</b> активные группы прямо сейчас?
            Окно тишины игнорируется (ручное действие). Пустые проекты пропускаются.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setBroadcastOpen(false)} disabled={broadcasting}>Отмена</Button>
            <Button color="blue" onClick={doBroadcast} loading={broadcasting}>Разослать</Button>
          </Group>
        </Stack>
      </Modal>
    </AppLayout>
  )
}

/** Строка-редактор настроек одной группы. Локальный драфт → Save = PATCH. */
function GroupRow({ group, onSaved }: { group: TgChatBindingDto; onSaved: () => void }) {
  const [s, setS] = useState<TgNotificationSettingsDto>(group.notifications)
  const [saving, setSaving] = useState(false)

  const startBad = s.window_enabled && !HHMM.test(s.window_start)
  const endBad = s.window_enabled && !HHMM.test(s.window_end)
  const orderBad = s.window_enabled && HHMM.test(s.window_start) && HHMM.test(s.window_end) && s.window_start >= s.window_end
  const digestBad = s.digest_enabled && !HHMM.test(s.digest_time)
  const invalid = startBad || endBad || orderBad || digestBad
  const dirty = JSON.stringify(s) !== JSON.stringify(group.notifications)

  const toggleDay = (i: number) => {
    const arr = s.days.split('')
    arr[i] = arr[i] === '1' ? '0' : '1'
    setS({ ...s, days: arr.join('') })
  }

  async function save() {
    setSaving(true)
    try {
      await updateTgNotificationSettings(group.id, {
        window_enabled: s.window_enabled,
        window_start: s.window_start,
        window_end: s.window_end,
        days: s.days,
        digest_enabled: s.digest_enabled,
        digest_time: s.digest_time,
      })
      notifications.show({ color: 'green', message: 'Настройки сохранены' })
      onSaved()
    } catch (e) {
      notifications.show({ color: 'red', title: 'Ошибка', message: String(e) })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Table.Tr>
      <Table.Td>
        <Text fw={600}>{group.project?.name ?? '—'}</Text>
        <Text size="xs" c="dimmed">
          {group.chat_title ?? group.tg_chat_id}{group.topic_name ? ` · ${group.topic_name}` : ''}
        </Text>
      </Table.Td>
      <Table.Td>
        <Stack gap={6}>
          <Switch
            checked={s.window_enabled}
            onChange={(e) => setS({ ...s, window_enabled: e.currentTarget.checked })}
            label={s.window_enabled ? 'Включено' : 'Выключено (шлём всегда)'}
            size="sm"
          />
          {s.window_enabled && (
            <>
              <Group gap="xs">
                <TextInput w={84} size="xs" label="с" value={s.window_start} error={startBad} onChange={(e) => setS({ ...s, window_start: e.currentTarget.value })} />
                <TextInput w={84} size="xs" label="до" value={s.window_end} error={endBad || orderBad} onChange={(e) => setS({ ...s, window_end: e.currentTarget.value })} />
              </Group>
              <Group gap={4}>
                {DAY_LABELS.map((d, i) => (
                  <Checkbox key={d} label={d} size="xs" checked={s.days[i] === '1'} onChange={() => toggleDay(i)} />
                ))}
              </Group>
            </>
          )}
        </Stack>
      </Table.Td>
      <Table.Td>
        <Stack gap={6}>
          <Switch
            checked={s.digest_enabled}
            onChange={(e) => setS({ ...s, digest_enabled: e.currentTarget.checked })}
            label={s.digest_enabled ? 'Включён' : 'Выключен'}
            size="sm"
          />
          {s.digest_enabled && (
            <TextInput w={84} size="xs" label="в" value={s.digest_time} error={digestBad} onChange={(e) => setS({ ...s, digest_time: e.currentTarget.value })} />
          )}
        </Stack>
      </Table.Td>
      <Table.Td>
        {dirty && (
          <Button size="xs" onClick={save} loading={saving} disabled={invalid}>Сохранить</Button>
        )}
        {!s.window_enabled && !dirty && <Badge variant="light" color="gray">без ограничений</Badge>}
      </Table.Td>
    </Table.Tr>
  )
}

/**
 * #9 — секция «Личная сводка»: выбрать сотрудников → разослать им в личку статус по
 * всем их проектам. Отчёт (кому ушло / кому нет и почему) — синхронный pre-flight.
 */
function PersonalDigestSection() {
  const [opts, setOpts] = useState<{ value: string; label: string }[]>([])
  const [selected, setSelected] = useState<string[]>([])
  const [sending, setSending] = useState(false)
  const [report, setReport] = useState<PersonalDigestReport | null>(null)

  useEffect(() => {
    void fetchUsers({ active: true })
      .then((us) => setOpts(us.map((u) => ({ value: u.id, label: u.full_name }))))
      .catch((e) => notifications.show({ color: 'red', title: 'Ошибка', message: String(e) }))
  }, [])

  async function send() {
    setSending(true)
    setReport(null)
    try {
      const r = await sendPersonalDigest(selected)
      setReport(r)
      notifications.show({ color: 'green', message: `В очереди: ${r.queued.length}, пропущено: ${r.skipped.length}` })
    } catch (e) {
      notifications.show({ color: 'red', title: 'Ошибка', message: String(e) })
    } finally {
      setSending(false)
    }
  }

  return (
    <Card withBorder p="md" radius="md" mb="md">
      <Group gap="xs" mb="sm">
        <IconUserCheck size={20} />
        <Text fw={600}>Личная сводка сотруднику в личку</Text>
      </Group>
      <Text size="sm" c="dimmed" mb="sm">
        Выбранным сотрудникам бот пришлёт в личку статус по всем их проектам (как «@bot статус»).
        Требуется, чтобы сотрудник нажал /start у бота — кому не дошло, увидите в отчёте.
      </Text>
      <Group align="flex-end" gap="sm">
        <MultiSelect
          placeholder="Кому разослать"
          data={opts}
          value={selected}
          onChange={setSelected}
          searchable
          clearable
          w={420}
          maw="100%"
        />
        <Button onClick={send} loading={sending} disabled={selected.length === 0}>
          Разослать ({selected.length})
        </Button>
      </Group>

      {report && (
        <Stack gap={4} mt="md">
          {report.queued.length > 0 && (
            <Alert color="green" variant="light" title={`Отправляется: ${report.queued.length}`}>
              {report.queued.map((q) => q.full_name).join(', ')}
            </Alert>
          )}
          {report.skipped.length > 0 && (
            <Alert color="orange" variant="light" title={`Не доставлено: ${report.skipped.length}`}>
              <Stack gap={2}>
                {report.skipped.map((s) => (
                  <Text key={s.user_id} size="sm">
                    {s.full_name} — {SKIP_REASON[s.reason] ?? s.reason}
                  </Text>
                ))}
              </Stack>
            </Alert>
          )}
        </Stack>
      )}
    </Card>
  )
}
